import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveSessionToken } from '../utils/authToken';

const API = `${window.location.origin}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [pwaHandoff, setPwaHandoff] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);

        if (!sessionIdMatch) {
          console.error('No session_id found in URL');
          navigate('/auth', { replace: true });
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Check for PWA nonce in the URL query params
        const urlParams = new URLSearchParams(window.location.search);
        const pwaNonce = urlParams.get('pwa_nonce');

        // Exchange session_id for session_token + user
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) throw new Error('Failed to exchange session');

        const data = await response.json();

        // Always save the session token to localStorage as a fallback
        // (iOS Safari may not persist the httpOnly cookie due to ITP)
        saveSessionToken(data.session_token);

        // ── iOS PWA nonce bridge ──────────────────────────────────────────
        // If a pwa_nonce is present, this callback is running in Safari
        // (opened from the PWA). Store the session on the server keyed by
        // the nonce so the PWA can claim it when the user switches back.
        if (pwaNonce) {
          try {
            await fetch(`${API}/auth/pwa-auth-complete`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                nonce: pwaNonce,
                session_token: data.session_token,
              }),
            });
          } catch (e) {
            console.error('Failed to store PWA auth relay:', e);
          }
          window.history.replaceState(null, '', window.location.pathname);
          setPwaHandoff(true);
          return;
        }
        // ──────────────────────────────────────────────────────────────────

        // Normal browser flow — cookie + localStorage are both set
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard', { replace: true, state: { user: data.user } });

      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/auth', { replace: true });
      }
    };

    processSession();
  }, [navigate]);

  // iOS PWA handoff screen — shown in Safari after auth
  if (pwaHandoff) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-6">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M16 3L28 9v14L16 29 4 23V9L16 3z" fill="white" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2" data-testid="pwa-handoff-title">You're signed in!</h1>
        <p className="text-zinc-400 mb-8 max-w-xs">
          Switch back to the <strong className="text-white">Blackified app</strong> on your home screen to continue.
        </p>
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <p className="text-xs text-zinc-600">
            Tap the Blackified icon on your home screen to return to the app.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-zinc-400 font-mono text-sm">Signing you in...</p>
      </div>
    </div>
  );
}
